import { FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { ReactElement, memo, useContext, useRef } from "react";
import { SDK_SuperTitle, strings } from "@quickpoint.me/shared";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ElementProps } from "../../functionalComponents/RenderPage";
import { HostId } from "../../state/ui";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import { OwnerName } from "../../state/selectors";
import { SuperTitleProps } from "../../@types/viewerati";
import { mainCssRules } from "../../lib/cssTemplates";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { opacityHelper } from "../../lib/cssHelpers";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import { useRecoilValue } from "recoil";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

const StyledSuperTitle = styled.p<SuperTitleProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  margin: 0;
  flex-wrap: wrap;
  text-wrap: auto;
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`};
  ${(props) => `${modifierStyled("supertitle:", props)}`}
`;

const supertitleFilter = (theme: DefaultTheme): SDK_SuperTitle => {
	return theme.supertitle;
};

const SuperTitle = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);

	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const elRef = useRef(null);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const hostId = useRecoilValue(HostId);
	const ownerName = useRecoilValue(OwnerName);
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;

	const baseFontSize = pageSize.baseFontSize;

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	if (!elementData.content) return <></>;

	return (
		<ThemeProvider theme={supertitleFilter}>
			<StyledSuperTitle
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				className={`${strings.css.pageElement}  supertitle`}
				data-atom-id={atomId}
				ref={elRef}
			>
				<span>{content}</span>
			</StyledSuperTitle>
		</ThemeProvider>
	);
};

export default memo(SuperTitle);
